import { IconDefinition, faPenLine, faPlay } from '@fortawesome/pro-solid-svg-icons';
import { useFeatureGate } from '@statsig/react-bindings';
import classNames from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { TooltipProps } from '@growtherapy/sprout-ui';

import { needsReLoginAtom } from '../../auth/state';
import { ControlPanelButton, ControlPanelButtonTooltip } from '../../components';
import { logger } from '../../datadog';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { RecordingControl as BaseRecordingControl } from '../../session/controls/RecordingControl';
import { RecordingControlMultipleParticipantsToast } from '../../session/controls/RecordingControlMultipleParticipantsToast';
import {
  PATIENT_OPTED_OUT_OF_SESSION_SUMMARIZATION,
  TRANSCRIPTION_DISABLED_TOAST_TIMEOUT,
  TRANSCRIPTION_NOT_AVAILABLE_FOR_MULTIPLE_PARTICIPANTS,
  TRANSCRIPTION_PAUSED_LOGIN_REQUIRED,
} from '../../session/controls/constants';
import { useShouldDisableTranscriptionForMultipleParticipants } from '../../session/controls/useShouldDisableTranscriptionForMultipleParticipants';
import { patientConsentAtom } from '../../session/messages/state';
import { telehealthVisuallyDistinctPausedRecordingControlGate } from '../../statsig/gates';
import { toast } from '../../toasts';
import { UserType } from '../../types';
import { AudioRecordingState, ConsentStatus, ConsentUpdateSource, participantCountAtom } from '../../video-sdk';
import { useSendProviderParticipantStateCallback } from '../messages/useSendProviderParticipantStateCallback';
import { inSessionPatientInformationAtom, providerShortIdAtom } from '../state';
import { useCachedPatientByShortId } from '../useGetPatientByShortId';
import { ConsentModal } from './ConsentModal';
import { MinorTranscriptionAcknowledgement } from './MinorTranscriptionAcknowledgement';
import { useCloseClientConsentPromptToastCallback } from './useClientConsentPromptToastCallback';
import { useCombineAudioTrackMediaStreams } from './useCombineAudioTracks';
import { useGetProviderPatientConsent } from './useGetProviderPatientConsent';
import { useCloseMinorRecordingPromptToastCallback } from './useMinorRecordingPromptToastCallback';
import { useRecordProviderTranscriptConsentMutation } from './useRecordProviderTranscriptionConsentMutation';
import { useRecordingControl } from './useRecordingControl';
import { useRequestingClientConsentToast } from './useRequestingClientConsentToast';
import { useStartRecordingCallback } from './useStartRecordingCallback';
import { useStopRecordingCallback } from './useStopRecordingCallback';

/**
 * TODO: Extract contents to parent once feature is launched
 */
function CombineAudioTrackMediaStreams() {
  useCombineAudioTrackMediaStreams();

  return null;
}

export function RecordingControl() {
  const { userClicked } = useTrackEvent();
  const { value: shouldUseDistinctPausedStyle } = useFeatureGate(telehealthVisuallyDistinctPausedRecordingControlGate);
  const hasMultipleParticipants = useAtomValue(participantCountAtom) > 1;

  const getAppointmentShortId = useAtomCallback(
    (get) => get(inSessionPatientInformationAtom)?.inSessionAppointmentShortId,
  );

  useRequestingClientConsentToast();
  const { patientShortId } = useAtomValue(inSessionPatientInformationAtom) ?? {};
  const providerShortId = useAtomValue(providerShortIdAtom);
  const providerNeedsReLogin = useAtomValue(needsReLoginAtom);
  const setClientTranscriptionConsent = useSetAtom(patientConsentAtom);
  const [hasDisabledTranscriptionForMultipleParticipants, setHasDisabledTranscriptionForMultipleParticipants] =
    useState(false);
  const [isMinorAcknowledgmentStepOpen, setIsMinorAcknowledgmentStepOpen] = useState(false);
  const [hasAcknowledgedClientMinor, setHasAcknowledgedClientMinor] = useState(false);
  const { data: cachedPatientData } = useCachedPatientByShortId(patientShortId);
  // If cache miss, skip the acknowledgment step; it's more confusing if the
  // client *isn't* actually a minor if the step is shown. We could consider a
  // more robust fallback path in the future, but this is extreme edge case
  // territory.
  const isClientUserMinor = cachedPatientData?.patientChart?.clientUser?.isMinor ?? false;
  const shouldShowMinorAcknowledgmentStep = isClientUserMinor && !hasAcknowledgedClientMinor;
  const sendParticipantState = useSendProviderParticipantStateCallback();
  const { recordingState, isVisible } = useRecordingControl();
  const startRecording = useStartRecordingCallback();
  const stopRecording = useStopRecordingCallback();
  const closeClientConsentPromptToast = useCloseClientConsentPromptToastCallback();
  const closeMinorRecordingPromptToast = useCloseMinorRecordingPromptToastCallback();

  const recordProviderConsent = useRecordProviderTranscriptConsentMutation(providerShortId, patientShortId);
  const { providerConsent, patientConsent, patientUpdateSource } = useGetProviderPatientConsent(
    providerShortId,
    patientShortId,
  );

  const requestingClientConsent = patientConsent === ConsentStatus.DECIDING;
  const isRecording = recordingState === AudioRecordingState.RECORDING;

  const [showConsentModal, setShowConsentModal] = useState(false);
  const isTranscriptionOptedOutInClientPortal =
    patientUpdateSource === ConsentUpdateSource.CLIENT_PORTAL && patientConsent === ConsentStatus.OPTED_OUT;

  const toggleRecording = useCallback(
    async (shouldRecord: boolean) => {
      logger.info(`Recording toggled ${shouldRecord ? 'on' : 'off'}`, {
        providerShortId,
        patientShortId,
        appointmentShortId: getAppointmentShortId()!,
      });
      if (shouldRecord) {
        await startRecording();
      } else {
        await stopRecording();
      }
      sendParticipantState();
    },
    [getAppointmentShortId, patientShortId, providerShortId, startRecording, stopRecording, sendParticipantState],
  );

  /**
   * The below code is to prevent sessions with multiple participants from surfacing
   * Transcription features due to AI quality issues identified in these types of sessions.
   * It is behind a feature flag and can be removed when we improve AI features for these sessions.
   */

  const shouldShowMultipleParticipantTranscriptionDisabled = useShouldDisableTranscriptionForMultipleParticipants();

  const shouldDisableTranscription =
    isRecording &&
    !hasDisabledTranscriptionForMultipleParticipants &&
    shouldShowMultipleParticipantTranscriptionDisabled;

  useEffect(
    function disableTranscriptionForMultipleParticipants() {
      const handleDisableTranscription = async () => {
        logger.info('Recording automatically disabled for multi-participant session', {
          providerShortId,
          patientShortId,
          appointmentShortId: getAppointmentShortId()!,
        });
        await toggleRecording(false);
        toast.enqueue(RecordingControlMultipleParticipantsToast, {
          timeout: TRANSCRIPTION_DISABLED_TOAST_TIMEOUT,
        });
        setHasDisabledTranscriptionForMultipleParticipants(true);
      };

      if (shouldDisableTranscription) {
        handleDisableTranscription();
      }
      if (!hasMultipleParticipants) {
        setHasDisabledTranscriptionForMultipleParticipants(false);
      }
    },
    [
      getAppointmentShortId,
      hasMultipleParticipants,
      isRecording,
      providerShortId,
      patientShortId,
      shouldDisableTranscription,
      toggleRecording,
    ],
  );

  const requestClientConsent = useCallback(() => {
    logger.info('Requesting client consent for AI transcription', {
      providerShortId,
      patientShortId,
      appointmentShortId: getAppointmentShortId()!,
    });
    setClientTranscriptionConsent({
      value: ConsentStatus.DECIDING,
      lastUpdated: Date.now(),
    });
    sendParticipantState();
  }, [getAppointmentShortId, patientShortId, providerShortId, setClientTranscriptionConsent, sendParticipantState]);

  // If a client is a minor, we are legally required to confirm the parent or
  // guardian is present before recording the session, even if the client has
  // consented to transcription already. The first time a provider tries to
  // toggle recording in a session with a minor, they will be required to
  // acknowledge that the parent or guardian is present before recording can
  // start. This is also necessary if the client has not yet consented, since
  // recording starts automatically if they agree and consent. A provider CANNOT
  // start recording without acknowledging the parent or guardian.
  const handleMinorAcknowledgmentStepCancel = useCallback(() => {
    logger.info('Provider declined to acknowledge parent or guardian is present', {
      providerShortId,
      patientShortId,
      appointmentShortId: getAppointmentShortId()!,
    });
    setIsMinorAcknowledgmentStepOpen(false);
    setHasAcknowledgedClientMinor(false);
  }, [getAppointmentShortId, patientShortId, providerShortId]);

  const handleMinorAcknowledgmentStepConfirm = useCallback(() => {
    logger.info('Provider acknowledged parent or guardian is present', {
      providerShortId,
      patientShortId,
      appointmentShortId: getAppointmentShortId()!,
      patientConsent,
    });
    setIsMinorAcknowledgmentStepOpen(false);
    setHasAcknowledgedClientMinor(true);

    if (patientConsent === ConsentStatus.OPTED_IN) {
      logger.info('Client has opted in, starting recording', {
        providerShortId,
        patientShortId,
        appointmentShortId: getAppointmentShortId()!,
      });
      toggleRecording(true);
      userClicked(EventTypeEnum.CLICK_RECORDING_TOGGLE, {
        appointmentShortId: getAppointmentShortId()!,
        newRecordingStatus: 'PLAY',
        entityType: UserType.PROVIDER,
      });
    } else {
      requestClientConsent();
    }
  }, [
    getAppointmentShortId,
    patientConsent,
    providerShortId,
    patientShortId,
    toggleRecording,
    requestClientConsent,
    userClicked,
  ]);

  const tooltipProps = useMemo(
    () =>
      isMinorAcknowledgmentStepOpen
        ? ({
            className: '!bg-transparent opacity-100',
            isOpen: true,
            clickable: true,
            text: (
              <MinorTranscriptionAcknowledgement
                hasConsent={patientConsent === ConsentStatus.OPTED_IN}
                onCancel={handleMinorAcknowledgmentStepCancel}
                onConfirm={handleMinorAcknowledgmentStepConfirm}
              />
            ),
          } satisfies Partial<TooltipProps>)
        : undefined,
    [
      isMinorAcknowledgmentStepOpen,
      handleMinorAcknowledgmentStepCancel,
      handleMinorAcknowledgmentStepConfirm,
      patientConsent,
    ],
  );

  const recordOptIn = async () => {
    const { data } = await recordProviderConsent(true);
    const providerIsOptedIn = data?.recordProviderTranscriptionConsent?.isOptedIn;
    if (providerIsOptedIn && shouldShowMinorAcknowledgmentStep) {
      setIsMinorAcknowledgmentStepOpen(true);
    } else if (providerIsOptedIn && patientConsent !== ConsentStatus.OPTED_IN) {
      requestClientConsent();
    } else if (providerIsOptedIn && patientConsent === ConsentStatus.OPTED_IN) {
      logger.info(
        'Provider opted in to AI transcription and client has already opted in, starting recording automatically',
        {
          providerShortId,
          patientShortId,
          appointmentShortId: getAppointmentShortId()!,
        },
      );
      toggleRecording(true);
      userClicked(EventTypeEnum.CLICK_RECORDING_TOGGLE, {
        appointmentShortId: getAppointmentShortId()!,
        newRecordingStatus: 'PLAY',
        entityType: UserType.PROVIDER,
      });
    }
    setShowConsentModal(false);
    userClicked(EventTypeEnum.GAVE_TRANSCRIPTION_CONSENT, {
      appointmentShortId: getAppointmentShortId()!,
      entityType: UserType.PROVIDER,
    });
  };

  const onRecordingToggle = useCallback(
    (shouldRecord: boolean) => {
      closeMinorRecordingPromptToast();
      if (shouldShowMinorAcknowledgmentStep && shouldRecord) {
        logger.info(
          'Provider manually toggled recording on with minor and has not yet acknowledged parent or guardian presence yet, showing acknowledgment step first',
          {
            providerShortId,
            patientShortId,
            appointmentShortId: getAppointmentShortId()!,
          },
        );
        setIsMinorAcknowledgmentStepOpen(true);
      } else {
        logger.info(`Provider manually toggled recording ${shouldRecord ? 'on' : 'off'}`, {
          providerShortId,
          patientShortId,
          appointmentShortId: getAppointmentShortId()!,
        });
        toggleRecording(shouldRecord);
        userClicked(EventTypeEnum.CLICK_RECORDING_TOGGLE, {
          appointmentShortId: getAppointmentShortId()!,
          newRecordingStatus: shouldRecord ? 'PLAY' : 'PAUSE',
          entityType: UserType.PROVIDER,
        });
      }
    },
    [
      closeMinorRecordingPromptToast,
      getAppointmentShortId,
      patientShortId,
      providerShortId,
      shouldShowMinorAcknowledgmentStep,
      toggleRecording,
      userClicked,
    ],
  );

  const onPendingConsentClick = useCallback(() => {
    closeClientConsentPromptToast();
    if (providerConsent !== ConsentStatus.OPTED_IN) {
      setShowConsentModal(true);
    } else if (shouldShowMinorAcknowledgmentStep) {
      logger.info(
        'Provider requested client transcription consent with minor and has not yet acknowledged parent or guardian presence yet, showing acknowledgment step first',
        {
          providerShortId,
          patientShortId,
          appointmentShortId: getAppointmentShortId()!,
        },
      );
      setIsMinorAcknowledgmentStepOpen(true);
    } else {
      requestClientConsent();
    }
  }, [
    closeClientConsentPromptToast,
    getAppointmentShortId,
    patientShortId,
    providerShortId,
    providerConsent,
    requestClientConsent,
    shouldShowMinorAcknowledgmentStep,
  ]);

  const bothPartiesConsented = patientConsent === ConsentStatus.OPTED_IN && providerConsent === ConsentStatus.OPTED_IN;

  if (!isVisible || providerConsent === ConsentStatus.OPTED_OUT) return null;

  return (
    <>
      {showConsentModal && (
        <ConsentModal onClose={() => setShowConsentModal(false)} recordOptIn={recordOptIn} isOpen={showConsentModal} />
      )}
      <CombineAudioTrackMediaStreams />
      {shouldShowMultipleParticipantTranscriptionDisabled ? (
        <DisabledRecordingControl icon={faPlay} tooltipText={TRANSCRIPTION_NOT_AVAILABLE_FOR_MULTIPLE_PARTICIPANTS} />
      ) : isTranscriptionOptedOutInClientPortal ? (
        <DisabledRecordingControl icon={faPenLine} tooltipText={PATIENT_OPTED_OUT_OF_SESSION_SUMMARIZATION} />
      ) : providerNeedsReLogin ? (
        <DisabledRecordingControl icon={faPlay} tooltipText={TRANSCRIPTION_PAUSED_LOGIN_REQUIRED} />
      ) : (
        <BaseRecordingControl
          className={classNames({
            'bg-lilac-700 hover:bg-lilac-600': bothPartiesConsented && (isRecording || !shouldUseDistinctPausedStyle),
            'text-coral-600': bothPartiesConsented && !isRecording && shouldUseDistinctPausedStyle,
          })}
          isRecording={isRecording}
          isRequestingConsent={requestingClientConsent}
          consentPending={!bothPartiesConsented}
          onRecordingToggle={onRecordingToggle}
          tooltipProps={tooltipProps}
          onPendingConsentClick={onPendingConsentClick}
        />
      )}
    </>
  );
}

function DisabledRecordingControl({ icon, tooltipText }: { icon: IconDefinition; tooltipText: string }) {
  return (
    <ControlPanelButtonTooltip
      text={tooltipText}
      /**
       * Although this Tooltip wraps a ControlPanelButton, the tooltip only ever
       * shows when the ControlPanelButton is disabled. In order to make this
       * tooltip still accessible for all users, we wrap it in a <div/> so that
       * there is an element to add the tooltip props to and set
       * childIsInteractive={false} (since the ControlPanelButton won't be
       * actually interactive when the tooltip is enabled).
       */
      childIsInteractive={false}
    >
      <div>
        <ControlPanelButton
          disabled={true}
          aria-label={`Transcription is disabled: ${tooltipText}`}
          iconDefinition={icon}
          data-testid='recording-control'
        />
      </div>
    </ControlPanelButtonTooltip>
  );
}
